import { useMutation } from '@apollo/client/react';
import { useTranslation } from 'react-i18next';

import {
    IPageDrawerProps,
    PageDrawer,
} from 'common/components/PageDrawer/PageDrawer';
import {
    IExtraCategoryOption,
    IExtraCategoryCreateInput,
    CreateExtraCategoryDocument,
} from 'graphql/types';
import { useApolloError } from 'common/hooks/useApolloError';
import { useSnackbar } from 'common/hooks/useSnackbar';
import { PageTitle } from 'common/components/PageTitle';
import { ExtraCategoryForm } from 'organisation/components/forms/ExtraCategoryForm';
import { formatColor } from 'common/utils/color';

interface IProps extends IPageDrawerProps {
    onClose?(): void;
}

export const ExtraCategoryCreateDrawer = ({ onClose, ...other }: IProps) => {
    const [translate] = useTranslation();
    const [displaySnackbar] = useSnackbar();
    const { showApolloError } = useApolloError();
    const [createExtraCategory, { loading: loadingCreate }] = useMutation(
        CreateExtraCategoryDocument
    );

    const handleSubmit = async (values: IExtraCategoryCreateInput) => {
        const { choiceOptions, filter, showLabel, color, ...otherValues } =
            values;

        let newValues: IExtraCategoryCreateInput = {
            color: formatColor(color),
            ...otherValues,
        };

        const isChoiceType =
            newValues.categoryType === IExtraCategoryOption.Choice;

        if (isChoiceType) {
            const newChoiceOptions = choiceOptions?.map((option) => ({
                ...option,
                color: formatColor(option.color),
            }));

            newValues = { ...newValues, choiceOptions: newChoiceOptions };
        }

        const isCheckboxType =
            newValues.categoryType === IExtraCategoryOption.Boolean;
        const isDateType =
            newValues.categoryType === IExtraCategoryOption.Datetime;

        // Checkboxes and date types are not used as label or filter
        if (!isCheckboxType && !isDateType) {
            newValues = { ...newValues, filter, showLabel };
        }

        try {
            await createExtraCategory({
                variables: { extraCategoryInput: newValues },
                // Refetch every active list/filter query with its current
                // variables so the new category shows up immediately
                // (e.g. in the certificate filter bar).
                refetchQueries: ['ExtraCategories', 'ExtraCategoryFilters'],
                awaitRefetchQueries: true,
            });

            displaySnackbar(
                translate(
                    'extraCategoriesDrawer.extraCategoryActionSuccess.create'
                ),
                {
                    variant: 'success',
                }
            );

            onClose?.();
        } catch (error) {
            showApolloError(error);
        }

        return;
    };

    const initialValues: IExtraCategoryCreateInput = {
        name: '',
        categoryType: IExtraCategoryOption.Choice,
        forModelTypes: [],
        choiceOptions: [],
        multipleChoices: false,
        showLabel: false,
        filter: false,
    };

    return (
        <PageDrawer onClose={onClose} {...other}>
            <PageTitle>
                {`${translate('extraCategoriesDrawer.pageTitle')} - ${translate(
                    'extraCategoriesDrawer.newExtraCategory'
                )}`}
            </PageTitle>

            <ExtraCategoryForm
                disabled={loadingCreate}
                initialValues={initialValues}
                onClose={onClose}
                onSubmit={handleSubmit}
            />
        </PageDrawer>
    );
};
